import { memo, useEffect } from 'react';
import { Chart } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    type ChartOptions,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';
import type { TFunction } from 'i18next';

import { cleanupCustomTooltip, createCustomTooltip } from '@/shared/lib/chart';
import { useTheme } from '@/shared/lib/hooks';
import { createDataLabelsPlugin } from '@/shared/ui/chart-data-labels-plugin';

import type { PatternHistogramSeries } from '../../model/types/chart';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
);

interface StatsData {
    label: string;
    probability70p?: number | null;
    threshold?: Array<number | null | undefined> | null;
    histogramData?: string | PatternHistogramSeries | undefined;
}

interface PatternStatsItemProps {
    stats: StatsData;
    patternName: string;
    variant: 'target' | 'failure';
    isDarkTheme: boolean;
    colorByLabel: Map<string, string>;
    t: TFunction<'pattern-studio', undefined>;
    isLastItem: boolean;
    hasMultipleStats: boolean;
}

const useHistogramCalculation = (
    histogramData?: string | PatternHistogramSeries,
) => {
    if (!histogramData) {
        return { avgHistogram: null, hasHistogramData: false };
    }

    let parsedData: PatternHistogramSeries | undefined;

    if (typeof histogramData === 'string') {
        try {
            parsedData = JSON.parse(histogramData) as PatternHistogramSeries;
        } catch {
            return { avgHistogram: null, hasHistogramData: false };
        }
    } else {
        parsedData = histogramData;
    }

    if (!parsedData || !Array.isArray(parsedData) || parsedData.length === 0) {
        return { avgHistogram: null, hasHistogramData: false };
    }

    const first = parsedData[0];

    if (
        !first?.bin_edges ||
        !first?.counts ||
        first.bin_edges.length === 0 ||
        first.counts.length === 0
    ) {
        return { avgHistogram: null, hasHistogramData: false };
    }

    // Calculate weighted average from histogram
    const counts = first.counts.slice(
        0,
        Math.min(first.counts.length, first.bin_edges.length - 1),
    );
    const { totalWeightedSum, totalCount } = counts.reduce(
        (acc, count, i) => {
            const safeCount = count || 0;
            const binStart = first.bin_edges[i] || 0;
            const binEnd = first.bin_edges[i + 1] || binStart;
            const binCenter = (binStart + binEnd) / 2;

            return {
                totalWeightedSum: acc.totalWeightedSum + binCenter * safeCount,
                totalCount: acc.totalCount + safeCount,
            };
        },
        { totalWeightedSum: 0, totalCount: 0 },
    );

    const avgHistogram = totalCount > 0 ? totalWeightedSum / totalCount : null;

    return {
        avgHistogram,
        hasHistogramData: true,
    };
};

const useThresholdCalculation = (
    threshold: Array<number | null | undefined>,
) => {
    const validValues = threshold.filter(
        (val): val is number => val != null && val !== undefined,
    );
    const nullCount = threshold.length - validValues.length;
    const shouldShowThreshold =
        threshold.length > 0 && nullCount < threshold.length / 2;
    const avgThreshold =
        validValues.length > 0
            ? validValues.reduce((sum, val) => sum + val, 0) /
              validValues.length
            : null;

    return {
        shouldShowThreshold,
        avgThreshold,
        hasValidThreshold: threshold.length > 0,
    };
};

const PatternStatsItem = memo(
    ({
        stats,
        patternName,
        variant,
        isDarkTheme,
        colorByLabel,
        t,
        isLastItem,
        hasMultipleStats,
    }: PatternStatsItemProps) => {
        const threshold = stats.threshold ?? [];
        const { shouldShowThreshold, avgThreshold } =
            useThresholdCalculation(threshold);
        const { avgHistogram, hasHistogramData } = useHistogramCalculation(
            stats.histogramData,
        );

        const containerStyle = {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '6px',
            marginBottom: hasMultipleStats ? 16 : 0,
            paddingBottom: hasMultipleStats && !isLastItem ? 16 : 0,
            borderBottom:
                hasMultipleStats && !isLastItem
                    ? `1px solid ${isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`
                    : 'none',
        };

        const headerStyle = {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: isDarkTheme ? '#fff' : '#000',
            fontWeight: 600,
        };

        const colorIndicatorStyle = {
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: 999,
            backgroundColor:
                colorByLabel.get(patternName) ||
                (isDarkTheme ? '#aaa' : '#666'),
        };

        return (
            <div style={containerStyle}>
                <div style={headerStyle}>
                    <span style={colorIndicatorStyle} />
                    <span>{patternName}</span>
                </div>

                {hasHistogramData && avgHistogram && (
                    <div>
                        <span />
                        {t(
                            variant === 'target'
                                ? 'patternStats.avgTargetShort'
                                : 'patternStats.avgFailureShort',
                            { avgCandles: Math.round(avgHistogram) },
                        )}
                    </div>
                )}

                {shouldShowThreshold && avgThreshold && (
                    <div>
                        <span />
                        {t(
                            variant === 'target'
                                ? 'patternStats.thresholdTargetShort'
                                : 'patternStats.thresholdFailureShort',
                            { avgCandles: Math.round(avgThreshold) },
                        )}
                    </div>
                )}
            </div>
        );
    },
);

PatternStatsItem.displayName = 'PatternStatsItem';

export type HistogramItem = {
    label: string;
    color?: string;
    series?: string | PatternHistogramSeries;
};

interface PatternCandleHistogramProps {
    items: HistogramItem[];
    height?: number;
    // When provided, the component will render descriptive sentences below the chart
    stats?: StatsData[];
    variant?: 'target' | 'failure';
}

export const PatternCandleHistogram = memo(
    ({
        items,
        height = 250,
        stats,
        variant = 'target',
    }: PatternCandleHistogramProps) => {
        const { t } = useTranslation('pattern-studio');
        const isDarkTheme = useTheme()[2];

        useEffect(
            () => () => {
                cleanupCustomTooltip();
            },
            [],
        );

        const defaultColors = [
            '#4285F4',
            '#EA4335',
            '#FBBC05',
            '#34A853',
            '#8430CE',
        ];

        // Trading platform colors for positive (target) and negative (failure)
        const positiveColor = '#26a69a'; // TRADING_PLATFORM_POSITIVE_COLOR
        const negativeColor = '#ef5350'; // TRADING_PLATFORM_NEGATIVE_COLOR

        type BuiltDataset = {
            label: string;
            color: string;
            counts: number[];
            edges: number[];
        };

        const parseSeries = (
            src?: string | PatternHistogramSeries,
        ): PatternHistogramSeries | undefined => {
            if (!src) return undefined;

            if (typeof src === 'string') {
                try {
                    return JSON.parse(src) as PatternHistogramSeries;
                } catch {
                    return undefined;
                }
            }

            return src;
        };

        const built: BuiltDataset[] = items.map((it, index) => {
            const series = parseSeries(it.series);
            const first =
                Array.isArray(series) && series.length > 0
                    ? series[0]!
                    : { counts: [], bin_edges: [] };
            const label = it.label || `Pattern ${index + 1}`;
            // Use trading platform color based on variant, or provided color, or default color
            let color: string;

            if (variant === 'target') {
                color = positiveColor;
            } else if (variant === 'failure') {
                color = negativeColor;
            } else {
                color =
                    it.color || defaultColors[index % defaultColors.length]!;
            }

            const roundedEdges = first.bin_edges.map((e) => Math.round(e));

            return { label, color, counts: first.counts, edges: roundedEdges };
        });

        const colorByLabel = new Map<string, string>(
            built.map((d) => [d.label, d.color]),
        );

        // Keep only datasets that actually have data (> 0 counts)
        const nonEmpty = built.filter(
            (d) =>
                Array.isArray(d.counts) &&
                d.counts.some((c) => typeof c === 'number' && c > 0),
        );

        if (nonEmpty.length === 0) {
            return null;
        }

        const longest = nonEmpty.reduce<BuiltDataset | null>((acc, cur) => {
            if (!acc) return cur;

            return cur.counts.length > acc.counts.length ? cur : acc;
        }, null);

        const labels: string[] =
            longest && longest.edges.length > 1
                ? longest.edges.slice(0, -1).map((start, i) => {
                      const end = longest.edges[i + 1]!;

                      return `${start}-${end}`;
                  })
                : [];

        if (labels.length === 0) {
            return null;
        }

        const chartData = {
            labels,
            datasets: nonEmpty.map((d) => ({
                label: d.label,
                data: d.counts,
                backgroundColor: `${d.color}80`,
                borderColor: d.color,
                borderWidth: 1,
                maxBarThickness: 50,
            })),
        };

        const options: ChartOptions<'bar'> = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: false,
                },
                legend: {
                    display: built.length > 1,
                    labels: {
                        color: isDarkTheme ? '#fff' : '#000',
                    },
                },
                tooltip: {
                    enabled: false,
                    external: createCustomTooltip({
                        isDark: isDarkTheme,
                        title: t('patternStats.candleHistogram'),
                        formatItems: (tooltipModel) =>
                            tooltipModel.dataPoints.map((point) => {
                                const datasetLabel = point.dataset.label || '';
                                const range =
                                    chartData.labels?.[point.dataIndex] ?? '';

                                return {
                                    color: point.dataset.borderColor as string,
                                    label: `${datasetLabel} • ${range}`,
                                    value: point.parsed.y,
                                };
                            }),
                    }),
                },
            },
            scales: {
                x: {
                    display: true,
                    title: {
                        display: true,
                        text: t('patternStats.candleRange'),
                        color: isDarkTheme ? '#fff' : '#000',
                    },
                    ticks: {
                        color: isDarkTheme ? '#fff' : '#000',
                    },
                    grid: {
                        color: isDarkTheme ? '#333' : '#e0e0e0',
                    },
                },
                y: {
                    display: true,
                    title: {
                        display: true,
                        text: t('patternStats.occurrences'),
                        color: isDarkTheme ? '#fff' : '#000',
                    },
                    ticks: {
                        color: isDarkTheme ? '#fff' : '#000',
                        stepSize: 1,
                        callback(value) {
                            return Number.isInteger(Number(value)) ? value : '';
                        },
                    },
                    grid: {
                        color: isDarkTheme ? '#333' : '#e0e0e0',
                    },
                },
            },
        };

        return (
            <div>
                <div style={{ height }}>
                    <Chart
                        data={chartData}
                        options={options}
                        plugins={[
                            createDataLabelsPlugin({
                                isDarkTheme,
                                displayType: 'percentage',
                                fontSize: '12px',
                            }),
                        ]}
                        type="bar"
                    />
                </div>

                {Array.isArray(stats) && stats.length > 0 && (
                    <div
                        style={{
                            marginTop: 16,
                            padding: '12px 16px',
                            backgroundColor: isDarkTheme
                                ? 'rgba(255, 255, 255, 0.05)'
                                : 'rgba(0, 0, 0, 0.02)',
                            borderRadius: '8px',
                            border: `1px solid ${isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'}`,
                        }}
                    >
                        {stats.map((s) => {
                            const patternName =
                                s.label ||
                                `Pattern ${Math.random().toString(36)}`;

                            return (
                                <PatternStatsItem
                                    key={s.label || Math.random().toString(36)}
                                    colorByLabel={colorByLabel}
                                    hasMultipleStats={stats.length > 1}
                                    isDarkTheme={isDarkTheme}
                                    isLastItem={
                                        stats.indexOf(s) === stats.length - 1
                                    }
                                    patternName={patternName}
                                    stats={s}
                                    t={t}
                                    variant={variant}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        );
    },
);

PatternCandleHistogram.displayName = 'PatternMultipleHistogram';
